/* eslint-disable @angular-eslint/no-input-rename */
import {
  IValidationService,
  VALIDATION_SERVICE_TOKEN,
  StateEnum,
  TValidators,
} from '@aksia/infrastructure';
import {
  afterNextRender,
  computed,
  Directive,
  effect,
  inject,
  input,
  linkedSignal,
  Signal,
  signal,
  untracked,
  WritableSignal,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { UiControlDirective } from './ui-control.directive';

@Directive({
  selector: '[validators]',
  standalone: true,
})
export class UiValidationDirective<T = unknown> {
  //#region Injections & Effects

  private readonly route = inject(ActivatedRoute);
  //private readonly uiInputDir = inject(InputDir);

  private readonly validationService: IValidationService = inject(
    VALIDATION_SERVICE_TOKEN,
  );
  private readonly valueEffect = effect(() => {
    let value = this.value();
    if (this.initialValue === Symbol.for('Not Set')) {
      this.initialValue = value;
    }
    untracked(() => {
      this.validate();
    });
  });

  //#endregion

  //#region Inputs/Outputs

  validators = input([], {
    transform: (validators: TValidators) => {
      let validatorsFn =
        typeof validators === 'function' ? validators() : validators;
      return validatorsFn;
    },
  });

  validationId = input(undefined, {
    alias: 'validation.id',
    transform: (validationId: number | string | undefined) => {
      return validationId ? `${validationId}` : '';
    },
  });

  overrideWarnings = input(undefined, {
    transform: (warnings: Array<string> | undefined) => {
      this.warnings.set(warnings ?? []);
      return warnings;
    },
  });
  overrideErrors = input(undefined, {
    transform: (errors: Array<string> | undefined) => {
      this.errors.set(errors ?? []);
      return errors;
    },
  });

  validationChain = input<Array<string>>([], { alias: 'validation.chain' });

  //#endregion

  //#region Properties

  host?: UiControlDirective;
  initialValue: unknown | Symbol = Symbol.for('Not Set');
  value: WritableSignal<T | undefined> = signal(undefined);
  errors = linkedSignal<Array<string>>(() => {
    return this.#validationResults()?.errors() ?? [];
  });
  warnings = linkedSignal<Array<string>>(() => {
    return this.#validationResults()?.warnings() ?? [];
  });
  state: Signal<StateEnum | undefined> = signal(undefined);
  tag?: string;
  #validationResults: WritableSignal<
    | {
        errors: Signal<Array<string>>;
        warnings: Signal<Array<string>>;
      }
    | undefined
  > = signal(undefined);

  calculatedState = computed(() =>
    (this.state?.() ?? this.errors?.()?.length! > 0)
      ? StateEnum.Error
      : this.warnings?.()?.length! > 0
        ? StateEnum.Warning
        : this.host?.stateService
          ? this.host?.stateService.state()
          : this.initialValue !== this.value()
            ? StateEnum.Dirty
            : StateEnum.Pristine,
  );

  updateValidationTagEffect = effect(() => {
    this.updateValidationTag(this.tag!, this.host?.tag()!);
  });

  //#endregion

  constructor() {
    afterNextRender(() => {
      this.registerValidation();
      this.validate();
    });
  }

  //#region Methods

  registerValidation() {
    if (
      this.validationService &&
      this.host?.tag() &&
      this.host?.uid() &&
      this.validators()?.length > 0
    ) {
      let validationId = this.validationId() ?? this.host?.uid();

      this.#validationResults.set(
        this.validationService.registerValidation(
          validationId
            ? `${this.host?.tag()}@${validationId}`
            : this.host?.tag(),
          this.value,
          this.validators(),
          this.validationChain(),
        ),
      );
    }
  }

  updateValidationTag(currentTag: string, newTag: string) {
    let validationId = this.validationId() ?? this.host?.uid();
    let validationCurrentTag = `${currentTag}@${validationId}`;
    this.tag = newTag;

    if (this.validationService?.validationExists(validationCurrentTag)) {
      let validationNewTag = `${newTag}@${validationId}`;
      this.validationService.updateValidationTag(
        validationCurrentTag,
        validationNewTag,
      );
      this.validationService.validate(validationNewTag, this.value());
    } else {
      this.#validationResults.set(
        this.validationService.validate(validationCurrentTag, this.value()),
      );
    }
  }

  validate() {
    let errors: Array<string> = [];
    let warnings: Array<string> = [];

    if (
      this.validationService &&
      this.host?.tag() &&
      this.validators()?.length > 0
    ) {
      //Stateful Validation

      let validationId = this.validationId() ?? this.host?.uid();
      let validationTag = `${this.tag}@${validationId}`;

      if (!this.validationService.validationExists(validationTag)) {
        this.registerValidation();
      }
      this.validationService.validate(validationTag, this.value());
    } else {
      //Stateless Validation
      this.validators()?.forEach((validator) => {
        let result = validator(this.value());
        errors.push(...result.errors);
        warnings.push(...result.warnings);
        this.warnings.set(warnings);
        this.errors.set(errors);
      });
    }
  }

  //#endregion
}
